import { useMutation, useQuery } from "@apollo/client/react"
import { lazy, Suspense, useState } from "react"
import { toast } from "sonner"
import { gql } from "~/__generated__"
import { useDocumentTitle } from "~/common/use-document-title"
import { TablePageLayout } from "~/layouts/table-page-layout"
import { Badge } from "~/ui/badge"
import { Button } from "~/ui/button"
import { Card, CardContent } from "~/ui/card"
import { GraphqlError } from "~/ui/graphql-error"
import { ScreenLoading } from "~/ui/screen-loading"
import { Tabs, TabsList, TabsTrigger } from "~/ui/tabs"

const JourneyMap = lazy(() => import("~/maps/journey-map").then((m) => ({ default: m.JourneyMap })))

const inputClass =
  "h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"

const logsQuery = gql(/* GraphQL */ `
  query InternalAdminJourneyLogs($hidden: Boolean, $puzzleWpId: String) {
    internalAdminJourneyLogs(first: 50, hidden: $hidden, puzzleWpId: $puzzleWpId) {
      nodes {
        id
        completedOn
        comment
        city
        state
        latitude
        longitude
        hiddenAt
        authorDisplayName
        photoThumbUrl
        puzzle {
          id
          wpId
          title
        }
      }
    }
  }
`)

const hideMutation = gql(/* GraphQL */ `
  mutation InternalAdminJourneyLogHide($input: InternalAdminJourneyLogHideInput!) {
    internalAdminJourneyLogHide(input: $input) {
      journeyLog {
        id
        hiddenAt
      }
    }
  }
`)

const unhideMutation = gql(/* GraphQL */ `
  mutation InternalAdminJourneyLogUnhide($input: InternalAdminJourneyLogUnhideInput!) {
    internalAdminJourneyLogUnhide(input: $input) {
      journeyLog {
        id
        hiddenAt
      }
    }
  }
`)

const deleteMutation = gql(/* GraphQL */ `
  mutation InternalAdminJourneyLogDelete($input: InternalAdminJourneyLogDeleteInput!) {
    internalAdminJourneyLogDelete(input: $input) {
      deletedId
    }
  }
`)

type FilterValue = "ALL" | "VISIBLE" | "HIDDEN"

const filters: Array<{ value: FilterValue; label: string }> = [
  { value: "ALL", label: "All" },
  { value: "VISIBLE", label: "Visible" },
  { value: "HIDDEN", label: "Hidden" },
]

export const InternalAdminLogsScreen = () => {
  useDocumentTitle("Journey logs")
  const [filter, setFilter] = useState<FilterValue>("VISIBLE")
  const [puzzleWpId, setPuzzleWpId] = useState("")

  const hiddenVar = filter === "ALL" ? null : filter === "HIDDEN"
  const { data, loading, error, refetch } = useQuery(logsQuery, {
    variables: { hidden: hiddenVar, puzzleWpId: puzzleWpId.trim() || null },
  })

  const [hide] = useMutation(hideMutation, { onError: () => null })
  const [unhide] = useMutation(unhideMutation, { onError: () => null })
  const [del] = useMutation(deleteMutation, { onError: () => null })

  const logs = data?.internalAdminJourneyLogs.nodes ?? []

  return (
    <TablePageLayout>
      <h1 className="text-2xl">Journey logs</h1>
      <hr className="my-4 border-0 border-b" />

      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={filter} onValueChange={(v) => setFilter(v as FilterValue)}>
          <TabsList>
            {filters.map((f) => (
              <TabsTrigger key={f.value} value={f.value}>
                {f.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <input
          placeholder="Filter by WP ID"
          value={puzzleWpId}
          onChange={(e) => setPuzzleWpId(e.target.value)}
          className={`${inputClass} w-48`}
        />
      </div>

      <div className="mt-6">
        {!data && loading ? (
          <ScreenLoading />
        ) : error ? (
          <GraphqlError error={error} />
        ) : logs.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-sm text-gray-600">No logs.</CardContent>
          </Card>
        ) : (
          <ul className="grid gap-3">
            {logs.map((log) => (
              <li key={log.id}>
                <Card>
                  <CardContent className="space-y-3 py-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex min-w-0 gap-3">
                        {log.photoThumbUrl ? (
                          <img
                            src={log.photoThumbUrl}
                            alt=""
                            className="h-16 w-16 shrink-0 rounded bg-stone-100 object-contain"
                          />
                        ) : (
                          <div className="h-16 w-16 shrink-0 rounded bg-gray-100" />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium">{log.puzzle.title}</span>
                            <Badge variant="secondary" className="font-mono text-[10px]">
                              {log.puzzle.wpId}
                            </Badge>
                            {log.hiddenAt ? <Badge variant="outline">HIDDEN</Badge> : null}
                          </div>
                          <div className="text-sm text-gray-600">
                            {log.authorDisplayName} ·{" "}
                            {new Date(log.completedOn).toLocaleDateString()}
                            {log.city || log.state
                              ? ` · ${[log.city, log.state].filter(Boolean).join(", ")}`
                              : null}
                          </div>
                          {log.comment ? (
                            <p className="mt-1 line-clamp-3 text-sm text-gray-800">{log.comment}</p>
                          ) : null}
                        </div>
                        {typeof log.latitude === "number" && typeof log.longitude === "number" ? (
                          <Suspense
                            fallback={
                              <div className="h-20 w-32 shrink-0 animate-pulse rounded bg-stone-100" />
                            }
                          >
                            <JourneyMap
                              stops={[
                                { id: log.id, latitude: log.latitude, longitude: log.longitude },
                              ]}
                              interactive={false}
                              initialZoom={9}
                              className="h-20 w-32 shrink-0"
                            />
                          </Suspense>
                        ) : (
                          <div className="flex h-20 w-32 shrink-0 items-center justify-center rounded bg-stone-50 text-[10px] uppercase tracking-wider text-stone-400">
                            no coords
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {log.hiddenAt ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            const r = await unhide({ variables: { input: { id: log.id } } })
                            if (r.data) {
                              toast.success("Restored")
                              refetch()
                            }
                          }}
                        >
                          Unhide
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            const r = await hide({ variables: { input: { id: log.id } } })
                            if (r.data) {
                              toast.success("Hidden")
                              refetch()
                            }
                          }}
                        >
                          Hide
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          if (!confirm("Permanently delete this log?")) return
                          const r = await del({ variables: { input: { id: log.id } } })
                          if (r.data) {
                            toast.success("Deleted")
                            refetch()
                          }
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </div>
    </TablePageLayout>
  )
}
